import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../../utils";
import Icon from "../custom/Icon";
import { Input } from "./input";

interface SearchInputProps {
	placeholder?: string;
	debounceMs?: number;
	onSearchChange?: (query: string) => void;
	onSubmit?: (query: string) => void;
	className?: string;
}

export function SearchInput({
	placeholder = "Search...",
	debounceMs = 300,
	onSearchChange,
	onSubmit,
	className = "",
}: SearchInputProps) {
	const [searchQuery, setSearchQuery] = useState("");
	const [isFocused, setIsFocused] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const timer = setTimeout(() => {
			onSearchChange?.(searchQuery);
		}, debounceMs);

		return () => clearTimeout(timer);
	}, [searchQuery, debounceMs, onSearchChange]);

	const handleSubmit = (e?: React.MouseEvent | React.KeyboardEvent) => {
		e?.preventDefault();
		if (onSubmit && searchQuery.trim()) {
			onSubmit(searchQuery);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSubmit();
		}
	};

	return (
		<div className={cn("relative w-full group", className)}>
			<motion.div
				animate={{
					scale: isFocused ? 1.1 : 1,
					color: isFocused ? "var(--primary)" : "currentColor",
				}}
				transition={{ type: "spring", stiffness: 400, damping: 17 }}
				className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10"
			>
				<Icon name="Search" className="size-4" />
			</motion.div>

			<Input
				ref={inputRef}
				type="text"
				placeholder={placeholder}
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				onKeyDown={handleKeyDown}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				className={cn(
					"pl-10 transition-all duration-300 mt-0",
					searchQuery.trim() && onSubmit ? "pr-10" : "",
					"w-full bg-background/50 backdrop-blur-sm border-muted-foreground/20 focus-visible:border-primary/50 focus-visible:ring-primary/20",
				)}
			/>

			<AnimatePresence>
				{searchQuery.trim() && onSubmit && (
					<motion.button
						key="submit-button"
						initial={{ opacity: 0, x: 12 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: 12 }}
						whileHover={{ x: -2 }}
						whileTap={{ x: 1 }}
						transition={{
							type: "spring",
							stiffness: 650,
							damping: 35,
							mass: 0.45,
						}}
						className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm z-10 flex items-center justify-center"
						onClick={(e) => handleSubmit(e)}
						type="button"
					>
						<Icon name="ArrowRight" className="size-3.5" />
					</motion.button>
				)}
			</AnimatePresence>
		</div>
	);
}

export default SearchInput;
